import { useState, useRef, useLayoutEffect, useEffect } from "react";

const PICKER_WIDTH = 352;
const PICKER_HEIGHT = 435;
const OFFSET = 8;

export const useEmojiPicker = () => {
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiPosition, setEmojiPosition] = useState({ top: 0, left: 0 });

  const anchorRef = useRef(null);
  const emojiPickerRef = useRef(null);

  const calculatePosition = () => {
    if (!anchorRef.current) return;

    const rect = anchorRef.current.getBoundingClientRect();

    const scrollX = window.pageXOffset || document.documentElement.scrollLeft;
    const scrollY = window.pageYOffset || document.documentElement.scrollTop;

    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    let top = rect.bottom + scrollY + OFFSET;
    let left = rect.left + scrollX;

    // Open upwards if not enough space
    if (rect.bottom + PICKER_HEIGHT > viewportHeight) {
      top = rect.top + scrollY - PICKER_HEIGHT - OFFSET;
    }

    // Shift left if overflowing right
    if (rect.left + PICKER_WIDTH > viewportWidth) {
      left = rect.right + scrollX - PICKER_WIDTH;
    }

    // Clamp to viewport
    left = Math.max(scrollX + OFFSET, left);
    top = Math.max(scrollY + OFFSET, top);

    setEmojiPosition({ top, left });
  };

  const openPicker = (event) => {
    anchorRef.current = event.currentTarget;
    setEmojiOpen(true);
  };

  const closePicker = () => {
    setEmojiOpen(false);
    anchorRef.current = null;
  };

  // Recalculate when opened
  useLayoutEffect(() => {
    if (emojiOpen) calculatePosition();
  }, [emojiOpen]);

  // Close on scroll
  useEffect(() => {
    if (!emojiOpen) return;

    const onScroll = (e) => {
      // ❌ ignore scrolls inside emoji picker
      if (emojiPickerRef.current?.contains(e.target)) {
        return;
      }

      closePicker();
    };

    // 👇 capture = true is required, but we filter target
    window.addEventListener("scroll", onScroll, true);

    return () => {
      window.removeEventListener("scroll", onScroll, true);
    };
  }, [emojiOpen]);

  // Close on outside click
  useEffect(() => {
    if (!emojiOpen) return;

    const handler = (e) => {
      if (
        emojiPickerRef.current &&
        !emojiPickerRef.current.contains(e.target) &&
        anchorRef.current &&
        !anchorRef.current.contains(e.target)
      ) {
        setEmojiOpen(false);
      }
    };

    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [emojiOpen]);

  return {
    emojiOpen,
    emojiPosition,
    emojiPickerRef,
    openPicker,
    closePicker,
  };
};
